/**
 * QuotaExhaustedBanner — bannière d'alerte affichée sur la page d'accueil
 * quand l'utilisateur a atteint son quota mensuel de sessions.
 *
 * - Acheteurs (`canBuyLicences` : ORG, ADM, GES) : CTA "Acheter un pack" → `ACHAT_PACK`
 *   (« Mon compte › Licences et Packs »), la même destination sur l'accueil et dans Pilot
 * - Autres profils : message les invitant à contacter l'organisateur ou le gestionnaire financier
 *
 * Cf. spec/rate-limiter-sessions.md §3.2
 */
import { useTranslation } from "react-i18next";
import { AlertTriangle, ShoppingCart } from "lucide-react";
import { Button } from "./ui/button";
import type { QuotaState } from "../hooks/useQuota";
import { useQuota } from "../hooks/useQuota";
import { formatDateAbonnement, libelleEtatAbonnement } from "../lib/subscription-labels";
import { canBuyLicences, ouvrirAchatDePack, type NavigationVersAchat } from "../lib/encadrant";

/**
 * Qui voit l'état de l'abonnement sur l'accueil : les RESPONSABLES seulement (arbitrage Valérie,
 * 29/09/2026). Un simple utilisateur n'a pas à apprendre que son entreprise a résilié ou n'a pas
 * payé ; il découvrira l'arrêt au lancement d'une session, avec un message qui l'explique.
 */
const PROFILS_INFORMES = new Set(["ORG", "ADM", "COO", "GES", "RH"]);

interface Props {
  userProfileType: string;
  /**
   * Navigation de l'application. Le bandeau choisit LUI-MÊME la destination d'achat
   * (`ouvrirAchatDePack`) : l'accueil ouvrait la page `buy-pack` neutralisée, Pilot « Mon compte »
   * — deux boutons identiques, deux destinations (correction n°5 du 30/09/2026).
   */
  onNavigate?: NavigationVersAchat;
}

export function QuotaExhaustedBanner({ userProfileType, onNavigate }: Props) {
  const { quota, loading } = useQuota();
  const { t } = useTranslation();

  if (loading || !quota) return null;
  if (quota.isKestioInternal || quota.envMode === "staging") return null;

  // Abonnement résilié, en pause ou terminé : ce n'est pas un dépassement de quota, et le
  // bandeau ne doit pas parler de « quota mensuel ». Sans ce cas, l'accueil restait MUET
  // (`limit` nul ⇒ retour null) et le client ne découvrait la coupure qu'au lancement.
  const etat = quota.subscriptionState;
  const afficherEtat =
    !!etat &&
    etat !== "active" &&
    etat !== "trial" &&
    PROFILS_INFORMES.has(userProfileType) &&
    // Un compte qui tourne sur des crédits achetés n'est pas coupé : annoncer « votre abonnement
    // a pris fin » en rouge lui dirait qu'il ne peut plus s'entraîner, alors qu'il peut.
    // ⚠️ DEUX mécanismes distincts : `totalPacksRemaining` ne somme que les packs de sessions
    // (`extra_session_packs`) ; les packs ONE-SHOT (Starter/Focus/Premium) vivent dans
    // `oneShotCredits`. N'en regarder qu'un laissait le bandeau mentir à l'autre moitié.
    quota.totalPacksRemaining === 0 &&
    (quota.oneShotCredits?.remaining ?? 0) === 0;

  // Règle PARTAGÉE avec le serveur (`canBuyLicences` : ORG, ADM, GES — Q3 du 29-30/09/2026) : la
  // copie locale (ORG/ADM/COO) montrait au COO un bouton que le serveur refusait, et le cachait au GES.
  const canPurchase = canBuyLicences(userProfileType);

  // ⚠️ Le bandeau d'état ne REMPLACE pas celui du quota : un compte en résiliation programmée,
  // encore payé, qui épuise ses 30 sessions du mois doit continuer à voir « acheter un pack » —
  // le serveur l'y autorise. Les deux informations cohabitent donc.
  const bandeauEtat = afficherEtat ? <BandeauAbonnement quota={quota} /> : null;
  const quotaEpuise = quota.limit != null && !quota.allowed;
  if (!quotaEpuise) return bandeauEtat;

  // Date de renouvellement (cycleEnd via la prop quota n'est pas exposée dans le hook,
  // on reste sur un message générique : "le mois prochain").
  return (
    <>
    {bandeauEtat}
    <div className="mt-4 rounded-xl border-2 border-red-300 bg-red-50 p-4 sm:p-5 max-w-3xl mx-auto text-left animate-in fade-in slide-in-from-top-2 duration-500">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 shrink-0">
          <AlertTriangle className="h-5 w-5 text-red-700" />
        </div>
        <div className="flex-1 space-y-2">
          <p className="font-bold text-red-900">
            {t("quota.bannerTitle", { used: quota.used, limit: quota.limit })}
          </p>
          <p className="text-sm text-red-800">
            {canPurchase ? t("quota.bannerBuyer") : t("quota.bannerNonBuyer")}
          </p>
          {canPurchase && onNavigate && (
            <Button
              onClick={() => ouvrirAchatDePack(onNavigate)}
              className="mt-2 bg-red-600 hover:bg-red-700 text-white font-semibold"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {t("quota.bannerCta")}
            </Button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

/**
 * Bandeau d'état d'abonnement (résilié, en pause, terminé). Distinct du bandeau de quota :
 * il ne propose pas d'acheter un pack, il explique et donne la date.
 */
function BandeauAbonnement({ quota }: { quota: QuotaState }) {
  const { t, i18n } = useTranslation();
  const fin = formatDateAbonnement(quota.subscriptionEndedAt, i18n.language);
  const reprise = formatDateAbonnement(quota.subscriptionResumesAt, i18n.language);

  // ⚠️ NE PAS lire `quota.allowed` ici (il vaut `true` pour un compte fermé sur cet appel sans
  // simulateur), NI redéduire l'accès d'une date : `accessEndsAt` est renseignée MÊME quand
  // l'accès est fermé — un abonnement expiré à échéance future s'affichait « accès jusqu'au
  // <date future> ». C'est le serveur qui tranche, et lui seul.
  const encoreOuvert = quota.subscriptionIsOpen === true;

  // Libellé calculé par la source unique, partagée avec l'étiquette de la fiche compte : les
  // deux copies avaient divergé (« Résilié » annoncé pour un impayé).
  const texte = libelleEtatAbonnement(
    {
      state: quota.subscriptionState ?? "ended",
      isOpen: encoreOuvert,
      finFormatee: fin,
      repriseFormatee: reprise,
    },
    t,
  );

  const couleurs = encoreOuvert
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-red-300 bg-red-50 text-red-900";

  return (
    <div className={`mt-4 rounded-xl border-2 p-4 sm:p-5 max-w-3xl mx-auto text-left ${couleurs}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/60 shrink-0">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <p className="font-semibold self-center">{texte}</p>
      </div>
    </div>
  );
}
